import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/auth';

const AuthContext = createContext(null);

export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  VIEWER: 'viewer',
  PERAWAT: 'perawat',
};

const ROLE_HIERARCHY = {
  admin: 3,
  user: 2,
  viewer: 1,
  perawat: 1,
};

// Default permission structure for each menu
const DEFAULT_MENU_PERMISSIONS = {
  active: true,
  add: false,
  edit: false,
  view: false,
  delete: false,
  print: false
};

// All available menus
export const ALL_MENUS = [
  { id: 'DASHBOARD', label: 'Dashboard', group: 'Dashboard' },
  { id: 'ACTIVITY_ADD', label: 'Add Activity', group: 'Activity' },
  { id: 'ACTIVITY_LIST', label: 'Activities', group: 'Activity' },
  { id: 'ACTIVITY_REPORT', label: 'Report Activity', group: 'Activity' },
  { id: 'TICKET_NEW', label: 'New Tickets', group: 'Ticketing' },
  { id: 'TICKET_LIST', label: 'Tickets', group: 'Ticketing' },
  { id: 'TICKET_REPORT', label: 'Report Tickets', group: 'Ticketing' },
  { id: 'FINANCE_AKUN', label: 'Master Akun', group: 'Keuangan' },
  { id: 'FINANCE_TRANSAKSI_ADD', label: 'Add Transaksi', group: 'Keuangan' },
  { id: 'FINANCE_TRANSAKSI', label: 'Transaksi In/Out', group: 'Keuangan' },
  { id: 'FINANCE_JURNAL', label: 'Jurnal Umum', group: 'Keuangan' },
  { id: 'FINANCE_LAPORAN', label: 'Laporan Akhir', group: 'Keuangan' },
  { id: 'USER_MASTER', label: 'Master User', group: 'User' },
  { id: 'USER_BAGIAN', label: 'Master Bagian', group: 'User' },
  { id: 'USER_LOG', label: 'Log Histories', group: 'User' },
  { id: 'USER_HAK_AKSES', label: 'Hak Akses', group: 'User' },
  { id: 'ANTRIAN_WEB', label: 'Antrian Klinik', group: 'Antrian' },
  { id: 'ANTRIAN_DISPLAY', label: 'Display TV', group: 'Antrian' },
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if already logged in on mount
  useEffect(() => {
    const savedUser = authService.getUser();
    const token = authService.getToken();
    if (savedUser && token) {
      setUser(savedUser);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authService.login(username, password);
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-logout jika token expired dari API response
  useEffect(() => {
    const handleTokenExpired = () => {
      console.warn('Token expired — auto logout');
      logout();
    };
    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, [logout]);

  // Role checking helpers
  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const hasMinRole = useCallback((minRole) => {
    if (!user) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minRole];
  }, [user]);

  const isAdmin = useCallback(() => user?.role === 'admin', [user]);

  // Permission-based access control
  const hasPermission = useCallback((menuId, action = 'view') => {
    if (!user) return false;
    // Admin has full access
    if (user.role === 'admin') return true;
    // Perawat: ONLY can access Antrian menus
    if (user.role === 'perawat') {
      if (menuId === 'ANTRIAN_WEB' || menuId === 'ANTRIAN_DISPLAY') return true;
      return false;
    }
    // Check specific permissions
    if (user.permissions && user.permissions[menuId]) {
      return user.permissions[menuId][action] === true;
    }
    // Viewer can view by default
    if (user.role === 'viewer' && action === 'view') return true;
    if (user.role === 'viewer') return false;
    // Default: user role can view, add, and active
    if (action === 'view' || action === 'add' || action === 'active') return true;
    return false;
  }, [user]);

  const canView = useCallback(() => !!user, [user]);
  const canCreate = useCallback(() => hasRole(ROLES.ADMIN, ROLES.USER), [hasRole]);
  const canEdit = useCallback(() => hasRole(ROLES.ADMIN, ROLES.USER), [hasRole]);
  const canDelete = useCallback(() => hasRole(ROLES.ADMIN), [hasRole]);
  const canManageUsers = useCallback(() => hasRole(ROLES.ADMIN), [hasRole]);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    clearError,
    isAuthenticated: !!user,
    hasRole,
    hasMinRole,
    isAdmin,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManageUsers,
    ALL_MENUS,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
